
import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { msalConfig } from './auth/msalConfig';
import './App.css';
import LanguageWrapper from './components/LanguageWrapper';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/AdminLayout';
import HomePage from './pages/HomePage';
import InquiryForm from './pages/InquiryForm';
import AppointmentScheduler from './pages/AppointmentScheduler';
import BookingCancelPage from './pages/BookingCancelPage';
import DashboardPage from './pages/DashboardPage';
import PortraitPage from './pages/PortraitPage';
import FaqPage from './pages/FaqPage';
import GlossaryPage from './pages/GlossaryPage';
import BlogListPage from './pages/BlogListPage';
import BlogDetailPage from './pages/BlogDetailPage';
import ImpressumPage from './pages/ImpressumPage';
import ContactPage from './pages/ContactPage';
import SubmissionUpdatePage from './pages/SubmissionUpdatePage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminContentEditor from './pages/admin/AdminContentEditor';
import AdminMedia from './pages/admin/AdminMedia';
import AdminInquiries from './pages/admin/AdminInquiries';
import AdminEmailTemplates from './pages/admin/AdminEmailTemplates';
import AdminSettings from './pages/admin/AdminSettings';
import AdminBookings from './pages/admin/AdminBookings';
import AdminBookingSettings from './pages/admin/AdminBookingSettings';
import { defaultLanguage } from './i18n';
import { preloadContentTypes } from './api/contentApi';

// Kick off content cache warming immediately so pages load without a data-fetch delay
preloadContentTypes(['portrait', 'faq', 'glossary', 'blog']);

// Only create MSAL instance if a client ID is configured
const msalClientId = process.env.REACT_APP_MSAL_CLIENT_ID;
export const msalInstance = msalClientId ? new PublicClientApplication(msalConfig) : null;

/**
 * Handles the redirect after MSAL login.
 * Checks sessionStorage for a saved admin path and navigates there.
 */
function MsalRedirectHandler({ children }: { children: React.ReactNode }) {
  const { inProgress, accounts } = useMsal();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      // Set the active account so getActiveAccount() works outside React components (e.g. adminApi.ts)
      if (msalInstance && !msalInstance.getActiveAccount() && accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }

      const redirectPath = sessionStorage.getItem('msalRedirectPath');
      if (redirectPath) {
        sessionStorage.removeItem('msalRedirectPath');
        window.location.replace(redirectPath);
      } else {
        setReady(true);
      }
    }
  }, [inProgress, accounts]);

  if (!ready) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return <>{children}</>;
}

function AppContent() {
  return (
    <HelmetProvider>
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
        <Router>
          <Routes>
            {/* Admin routes (Entra ID protected) — must be before /:lang to avoid matching */}
            <Route path="/admin" element={<ProtectedRoute><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/media" element={<ProtectedRoute><AdminLayout><AdminMedia /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/inquiries" element={<ProtectedRoute><AdminLayout><AdminInquiries /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/bookings" element={<ProtectedRoute><AdminLayout><AdminBookings /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/booking-settings" element={<ProtectedRoute><AdminLayout><AdminBookingSettings /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/email-templates" element={<ProtectedRoute><AdminLayout><AdminEmailTemplates /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><AdminLayout><AdminSettings /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/:contentType" element={<ProtectedRoute><AdminLayout><AdminContentEditor /></AdminLayout></ProtectedRoute>} />

            {/* Redirect root to default language */}
            <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

            {/* Language-prefixed public routes */}
            <Route path="/:lang" element={<LanguageWrapper />}>
              <Route index element={<Layout><HomePage /></Layout>} />
              <Route path="portrait" element={<Layout><PortraitPage /></Layout>} />
              <Route path="faq" element={<Layout><FaqPage /></Layout>} />
              <Route path="glossary" element={<Layout><GlossaryPage /></Layout>} />
              <Route path="blog" element={<Layout><BlogListPage /></Layout>} />
              <Route path="blog/:slug" element={<Layout><BlogDetailPage /></Layout>} />
              <Route path="impressum" element={<Layout><ImpressumPage /></Layout>} />
              <Route path="contact" element={<Layout><ContactPage /></Layout>} />
              <Route path="update/:id" element={<Layout><SubmissionUpdatePage /></Layout>} />
              <Route path="inquiry" element={<Layout><InquiryForm /></Layout>} />
              <Route path="appointment" element={<Layout><AppointmentScheduler /></Layout>} />
              <Route path="booking/:id" element={<Layout><BookingCancelPage /></Layout>} />
              <Route path="dashboard" element={<Layout><DashboardPage /></Layout>} />
            </Route>
          </Routes>
        </Router>
      </Suspense>
    </HelmetProvider>
  );
}

function App() {
  if (msalInstance) {
    return (
      <MsalProvider instance={msalInstance}>
        <MsalRedirectHandler>
          <AppContent />
        </MsalRedirectHandler>
      </MsalProvider>
    );
  }
  // Without MSAL config, public routes still work; admin routes will show auth error
  return <AppContent />;
}

export default App;
